import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

import Sidebar from '@/components/navigation/Sidebar';
import Header from '@/components/navigation/Header';
import { STORAGE_KEYS } from '@/utils/constants';
import { cn } from '@/utils/cn';

function MainLayout(): React.ReactElement {
  const location = useLocation();

  // Get initial sidebar state from localStorage
  const getInitialSidebarState = (): boolean => {
    const stored = localStorage.getItem(STORAGE_KEYS.SIDEBAR_COLLAPSED);
    return stored ? JSON.parse(stored) : false;
  };

  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarState);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu on route change - this is the primary mechanism
  useEffect(() => {
    setMobileMenuOpen(false);
    // Also ensure body styles are reset
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
    document.body.style.pointerEvents = '';
  }, [location.pathname]);


  const toggleSidebar = (): void => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem(STORAGE_KEYS.SIDEBAR_COLLAPSED, JSON.stringify(newState));
  };

  const toggleMobileMenu = (): void => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = useCallback((): void => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-background lg:flex">
      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
        isMobileOpen={mobileMenuOpen}
        onMobileClose={closeMobileMenu}
      />

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <Header onMenuClick={toggleMobileMenu} />

        {/* Page content with safe area insets */}
        <main className="flex-1 safe-area-inset-bottom">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className={cn(
              'page-container',
              // Reduce padding on mobile for better space utilization
              'px-4 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8'
            )}
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}

export default MainLayout;
